import { EventEmitter, Inject, Injectable } from "@angular/core";
import {ImagingUnit, ImagingUnitCount} from "../models/imagingUnit";
import {SignalrService} from "./signal-r.service";
import {HubConnection, HubConnectionState} from "@microsoft/signalr";
import {ImagingUnitService} from "./imagingUnit.service";
import { HttpClient } from "@angular/common/http";
import {PermissionDirective} from "src/app/directives/permission.directive";
import {first, groupBy, timeout, toArray} from "rxjs/operators";
import {catchError} from "rxjs/internal/operators/catchError";
import {Subscription} from "rxjs/internal/Subscription";
import { TimeoutError } from "rxjs/internal/operators/timeout";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { from } from "rxjs/internal/observable/from";
import {DeviceModel} from "../models/DeviceModel";
import {of} from "rxjs/internal/observable/of";
import {concatMap, count, mergeMap, zip} from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class ServerHubSignalR {

    public availableUnits: ImagingUnit[] = [];
    private eventSubscriptions: Subscription[] = [];
    public hasSubscription: boolean = false;
    public modelCounts: ImagingUnitCount[] = [];
    
    public unitsLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
    public isLoading: boolean = false;

    private signalrReady = false;
    private signalRService: SignalrService;
    private _serverHubConnection: HubConnection;
    public get serverHubConnection() {
        return this._serverHubConnection;
    }

    private _currentAccountChangedNotify: EventEmitter<any>;
    public setupCurrentAccountChangedNotify(notify: EventEmitter<any>) {
        this._currentAccountChangedNotify = notify;

        this.eventSubscriptions.push(this._currentAccountChangedNotify.subscribe(async () => {
            await this.getHasSubscriptions();
        }));
    }

    public get connectionReady(): boolean {
        return this.signalrReady && this.serverHubConnection.state === HubConnectionState.Connected;
    }

    constructor(
        @Inject('BASE_URL') public baseUrl: string, public imagingUnitService: ImagingUnitService, private _http: HttpClient
    ) {
        this._methods = {};

        this.unitsLoading.emit(true);
        this.isLoading = true;

        this.signalRService = new SignalrService(baseUrl);
    }

    public async setup() {
        await this.configureServerHub();
    }

    private async configureServerHub() {
        // Setup handler for UpdateStatus
        this.registerHandler('UpdateStatus', (async (microI: ImagingUnit) => {
            if (!microI) return; // ensure a not null record was sent

            let device = this.availableUnits.find(x => x.boardIdentifier == microI.boardIdentifier);
            if (!device) return; // ensure record was found

            // found
            device.online = microI.online;
            device.isBusy = microI.isBusy != null ? microI.isBusy : device.isBusy;
            device.wiFiIpaddress = microI.wiFiIpaddress != null ? microI.wiFiIpaddress : device.wiFiIpaddress;
            device.ethernetIpaddress = microI.ethernetIpaddress != null ? microI.ethernetIpaddress : device.ethernetIpaddress;
            
            this.updateModelCounts();
        }).bind(this));

        this.registerHandler('UpdateIsBusyStatus', (async (microI: ImagingUnit) => {
            if (!microI) return; // ensure a not null record was sent

            let device = this.availableUnits.find(x => x.boardIdentifier == microI.boardIdentifier);
            if (!device) return; // ensure record was found

            // found
            device.online = microI.online;
            device.isBusy = microI.isBusy != null ? microI.isBusy : device.isBusy;

            this.updateModelCounts();
        }).bind(this));

        await this.signalRService.CreateConnnectionWithReconnectWithClose('/hubs/server', connection => this.serverHubHandlers(connection), connection => this.serverHubConnected(connection), connection => this.setCaptureHubConnection(connection), () => this.serverHubDisconnected());
    }

    private setCaptureHubConnection(connection: HubConnection) {
        this._serverHubConnection = connection;
    }

    private serverHubDisconnected(): void {

        this.signalrReady = false;
    }

    private serverHubConnected(connection: HubConnection): void {

        this.signalrReady = true;

        this.callMethod("AddToServerGroup");

        this.notifyConnectedHandlers();
    }

    private serverHubHandlers(connection: HubConnection): void {
        // reattach handlers
        for (var methodName in this._methods) {
            let methods = this._methods[methodName];
            for (var i = 0; i < methods.length; i++) {
                let method = methods[i];
                connection.on(methodName, method);
            }
        }
    }

    private _methods: { [name: string]: ((...args: any[]) => void)[] };

    /** Registers a handler that will be invoked when the hub method with the specified method name is invoked.
       *
       * @param {string} methodName The name of the hub method to define.
       * @param {Function} handlerMethod The handler that will be raised when the hub method is invoked.
       */
    public registerHandler(methodName: string, handlerMethod: (...args: any[]) => void) {
        // Record handler so it can be applied again on reconnect
        if (!methodName || !handlerMethod) {
            return;
        }

        methodName = methodName.toLowerCase();
        if (!this._methods[methodName]) {
            this._methods[methodName] = [];
        }

        // Preventing adding the same handler multiple times.
        if (this._methods[methodName].indexOf(handlerMethod) !== -1) {
            return;
        }

        this._methods[methodName].push(handlerMethod);

        if (this.serverHubConnection) {
            this.serverHubConnection.on(methodName, handlerMethod);
        }
    }

    /** Removes the specified handler for the specified hub method.
       *
       * You must pass the exact same Function instance as was previously passed to {@link @microsoft/signalr.HubConnection.on}. Passing a different instance (even if the function
       * body is the same) will not remove the handler.
       *
       * @param {string} methodName The name of the method to remove handlers for.
       * @param {Function} handlerMethod The handler to remove. This must be the same Function instance as the one passed to {@link @microsoft/signalr.HubConnection.on}.
       */
    public unregisterHandler(methodName: string, handlerMethod: (...args: any[]) => void) {
        if (!methodName) {
            return;
        }

        methodName = methodName.toLowerCase();
        const handlers = this._methods[methodName];
        if (!handlers) {
            return;
        }
        if (handlerMethod) {
            const removeIdx = handlers.indexOf(handlerMethod);
            if (removeIdx !== -1) {
                handlers.splice(removeIdx, 1);
                if (handlers.length === 0) {
                    delete this._methods[methodName];
                }
            }
        } else {
            delete this._methods[methodName];
        }

        if (this.serverHubConnection) {
            this.serverHubConnection.off(methodName, handlerMethod);
        }
    }

    /** Invokes a hub method on the server using the specified name and arguments.
       *
       * The Promise returned by this method resolves when the server indicates it has finished invoking the method. When the promise
       * resolves, the server has finished invoking the method. If the server method returns a result, it is produced as the result of
       * resolving the Promise.
       *
       * @typeparam T The expected return type.
       * @param {string} methodName The name of the server method to invoke.
       * @param {any[]} args The arguments used to invoke the server method.
       * @returns {Promise<T>} A Promise that resolves with the result of the server method (if any), or rejects with an error.
       */
    public callMethod<T = any>(methodName: string, ...args: any[]): Promise<T> {
        if (this.connectionReady) {
            return this.serverHubConnection.invoke(methodName, ...args);
        }
        return new Promise<T>(null);
    }

    private _connectedHanders: Array<() => void> = [];

    public registerConnectedHandler(connectedHandler: () => void) {
        this._connectedHanders.push(connectedHandler);
    }

    private notifyConnectedHandlers() {
        if (this._connectedHanders) {
            for (var i = 0; i < this._connectedHanders.length; i++) {
                this._connectedHanders[i]();
            }
        }
    }

    /**
     * Inserts a device into availableUnits at the correct sorted position.
     */
    private insertAvailableUnit(unit: ImagingUnit) {
        let idx = 0;
        while (
            idx < this.availableUnits.length &&
            this.compareUnits(this.availableUnits[idx], unit) < 0
        ) {
            idx++;
        }
        this.availableUnits.splice(idx, 0, unit);
    }

    /**
     * Compare two ImagingUnit objects for ordering.
     */
    private compareUnits(a: ImagingUnit, b: ImagingUnit): number {
        // Direct connections first
        if (a.isLocalDevice && !b.isLocalDevice) return -1;
        if (!a.isLocalDevice && b.isLocalDevice) return 1;

        // Then by unit number
        const numA = this.extractUnitNumber(a);
        const numB = this.extractUnitNumber(b);
        if (numA !== numB) return numA - numB;

        // Fallback: by boardIdentifier string
        return (a.boardIdentifier || '').localeCompare(b.boardIdentifier || '');
    }

    /**
     * Extracts the numeric suffix from the boardIdentifier for sorting.
     */
    private extractUnitNumber(device: ImagingUnit): number {
        if (device.isLocalDevice) return Number.MIN_SAFE_INTEGER;
        if (!device.boardIdentifier) return Number.MAX_SAFE_INTEGER;

        const parts = device.boardIdentifier.split("-");
        const last = parts[parts.length - 1];
        const num = parseInt(last, 10);
        return isNaN(num) ? Number.MAX_SAFE_INTEGER : num;
    }

    private async getLocalDevices() {        
        var directlyConnectedDevice = await firstValueFrom(this.checkDevice("10.11.12.2"));

        if (directlyConnectedDevice && directlyConnectedDevice != "0") {
            if (this.availableUnits.findIndex(u => u.imagingUnitId == -1) == -1) {
                var device = new ImagingUnit();
                device.imagingUnitId = -1;
                device.serialNumber = "";
                device.boardIdentifier = "";
                device.wiFiIpaddress = "";
                device.ethernetIpaddress = "10.11.12.2";
                device.online = true;
                device.isActive = true;
                device.connectionId = "";
                device.isBusy = false;
                device.model = DeviceModel.ModelName;
                device.isLocalDevice = true;

                this.insertAvailableUnit(device);
            }
        } else {
            // remove the item from the array
            var idx = this.availableUnits.findIndex(u => u.imagingUnitId == -1);
            this.availableUnits.splice(idx, 1);
        }

        var hotspottedDevice = await firstValueFrom(this.checkDevice("10.11.12.1"));

        if (hotspottedDevice && hotspottedDevice != "0") {
            if (this.availableUnits.findIndex(u => u.imagingUnitId == -2) == -1) {
                var device = new ImagingUnit();
                device.imagingUnitId = -2;
                device.serialNumber = "";
                device.boardIdentifier = "";
                device.wiFiIpaddress = "10.11.12.1";
                device.ethernetIpaddress = "";
                device.online = true;
                device.isActive = true;
                device.connectionId = "";
                device.isBusy = false;
                device.model = DeviceModel.ModelName;
                device.isLocalDevice = true;

                this.insertAvailableUnit(device);
            }
        } else {
            // remove the item from the array
            var idx = this.availableUnits.findIndex(u => u.imagingUnitId == -2);
            this.availableUnits.splice(idx, 1);
        }
    }
    
    private async getUnits() {
        var units = await firstValueFrom(this.imagingUnitService.getAvailableUnits());

        if (units && units.length > 0) {
            // Remove any units not available (only for non-local devices)
            for (var i = this.availableUnits.length - 1; i >= 0; i--) {
                var unit = this.availableUnits[i];
                if (unit.imagingUnitId > 0) { // don't do anything with the local devices
                    if (units.findIndex(u => u.imagingUnitId == unit.imagingUnitId) == -1) {
                        this.availableUnits.splice(i, 1); // remove unit from available units
                    }
                }
            }

            // add or update existing units
            for (var i = 0; i < units.length; i++) {
                var unit = units[i];
                var existingUnitIndex = this.availableUnits.findIndex(u => u.imagingUnitId == unit.imagingUnitId);
                if (existingUnitIndex != -1) {
                    // replace record at index
                    this.availableUnits.splice(existingUnitIndex, 1, unit);
                } else {
                    // insert in order
                    this.insertAvailableUnit(unit);
                }
            }

            var models = await firstValueFrom(of(units).pipe(
                concatMap(x => x),
                groupBy(x => x.model),
                mergeMap(x => zip(of(x.key), x.pipe(count())))
                ).pipe(toArray()));

            if (models.length == 1) {
                DeviceModel.ModelName = models[0][0];
            } else {
                DeviceModel.Reset();
            }
        } else {
            this.clearAvailableUnits(); // clear the list of units
        }
    }

    async GetAvailableUnits() {
        this.unitsLoading.emit(true);
        this.isLoading = true;

        //this.clearAvailableUnits();

        if (!this.hasSubscription) {
            // if there is no subscription then do not load any units
            this.clearAvailableUnits(); // clear the list of units

            this.unitsLoading.emit(false);
            this.isLoading = false;
            return;
        }

        await this.getLocalDevices();
        await this.getUnits();
        this.updateModelCounts();

        this.unitsLoading.emit(false);
        this.isLoading = false;
    }

    private clearAvailableUnits() {
        this.availableUnits = [];
    }

    public async getHasSubscriptions() {
        this.hasSubscription = PermissionDirective.checkPermission({ requireSub: true, ignoreSuper: true });

        await this.GetAvailableUnits();
    }

    public checkDevice(ip: string) {
        return this._http
            .get(`https://${ip}`)
            .pipe(
                timeout(250),
                catchError(err => {
                    if (err instanceof TimeoutError) {
                        return "0";
                    } else {
                        return "1";
                    }
                })
            );
    }

    public updateModelCounts() {
        this.modelCounts = [];
        if (this.availableUnits && this.availableUnits.length >= 1) {
            from(this.availableUnits).pipe(
                groupBy(x => x.model)
            ).subscribe(async (group) => {
                let modelCount = new ImagingUnitCount();
                let devices = await firstValueFrom(group.pipe(toArray()));
                if (devices && devices.length >= 1) {
                    modelCount.online = devices.filter(x => !x.isLocalDevice && x.online).length;
                    modelCount.offline = devices.filter(x => !x.isLocalDevice && !x.online).length;
                    modelCount.model = group.key;
                    if (modelCount.online >= 1 || modelCount.offline >= 1)
                        this.modelCounts.push(modelCount);
                }
            });
        }
    }
}
